import { useEffect, useState } from 'react';
import eventService from '../services/eventService';

/**
 * The host's running-event count for the top bar's HOST bubble (round-3 handoff §1c).
 *
 * One module-level cache shared by every bar on the page, refreshed at most once a minute and
 * when the tab comes back into view — the bar mounts on every route and must not cost a
 * request each time. `primeHostSummary` lets a page that already has the host's events (the
 * dashboard) seed it, and `invalidateHostSummary` forces the next read after a publish or
 * cancel so the bubble does not lag a minute behind.
 */
const TTL_MS = 60_000;
const EMPTY = { liveEventCount: 0 };

let cache = { data: null, at: 0, inflight: null };
const listeners = new Set();

const publish = () => listeners.forEach((fn) => fn(cache.data));

export function primeHostSummary(data) {
  cache = { data: { ...EMPTY, ...data }, at: Date.now(), inflight: null };
  publish();
}

export function invalidateHostSummary() {
  cache = { ...cache, at: 0 };
  if (listeners.size) load();
}

/** Test seam. */
export function resetHostSummary() {
  cache = { data: null, at: 0, inflight: null };
}

function load() {
  if (cache.inflight) return cache.inflight;
  if (cache.data && Date.now() - cache.at < TTL_MS) return Promise.resolve(cache.data);
  cache.inflight = Promise.resolve()
    .then(() => eventService.getHostSummary())
    .then((r) => {
      if (r?.success && r.data) cache = { data: { ...EMPTY, ...r.data }, at: Date.now(), inflight: null };
      else cache = { ...cache, inflight: null };
      publish();
      return cache.data;
    })
    .catch(() => { cache = { ...cache, inflight: null }; return cache.data; });
  return cache.inflight;
}

export default function useHostSummary({ enabled = true } = {}) {
  const [data, setData] = useState(cache.data);
  useEffect(() => {
    if (!enabled) return undefined;
    listeners.add(setData);
    load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      listeners.delete(setData);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
  return data || EMPTY;
}
